import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Common } from '@trimblemaps/trimblemaps-js';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import {
  DataUpdated,
  DistanceUnits,
  RoutingProfile,
  VolumeUnits,
} from 'src/app/_models/daws/trip';
import { environment } from 'src/environments/environment';
import { AlertService } from '../auth/services/alert.service';
import { TripService } from './trip.service';
import { SubscriptionLevel } from '../auth/models/user';
import { AuthenticationService } from '../auth/services/authentication.service';
import { SearchService } from './search.service';
import { DataVersionService } from './data-version.service';
import { NumericKeyValueState } from '../_models/state/optionUIState';
import { UserService } from './user.service';
import { Precision } from '../application-settings/components/general/general.component';
export interface SearchView {
  includePlaces?: boolean;
  poiTypes?: string[];
  countries?: string[];
  states?: string[];
  excludeTypes?: string[];
  includeTypes?: string[];
  useCustomPlaces?: boolean;
  useMapCenter?: boolean;
  radiusFromCurrentLonLat?: number;
  noOfMaxResults?: number;
  precision?: Precision;
}

@Injectable({
  providedIn: 'root',
})
export class ApplicationSettingsService {
  private isLoading = new BehaviorSubject<boolean>(false);
  readonly isLoading$ = this.isLoading.asObservable();

  private selectedDistanceUnit = new BehaviorSubject<DistanceUnits>(null);
  readonly selectedDistanceUnit$ = this.selectedDistanceUnit.asObservable();

  private selectedCurrency = new BehaviorSubject<
    (typeof Common.TollCurrency)[keyof typeof Common.TollCurrency]
  >(null);
  readonly selectedCurrency$ = this.selectedCurrency.asObservable();

  private selectedFuelUnit = new BehaviorSubject<VolumeUnits>(null);
  readonly selectedFuelUnit$ = this.selectedFuelUnit.asObservable();
  private searchView = new BehaviorSubject<SearchView>(null);
  readonly searchView$ = this.searchView.asObservable();
  dataVersions: NumericKeyValueState[] = [];
  dataVersionList$ = this.dataversionService.dataVersions;
  get searchValue(): SearchView {
    return JSON.parse(sessionStorage.getItem('searchView'))
      ? JSON.parse(sessionStorage.getItem('searchView'))
      : this.searchView.getValue();
  }

  private store: BehaviorSubject<RoutingProfile> =
    new BehaviorSubject<RoutingProfile>(null);

  // shortcut to value
  private get storeValue(): RoutingProfile {
    return this.store.getValue();
  }
  readonly store$: Observable<RoutingProfile> = this.store.asObservable();

  private setStore(newStore: RoutingProfile) {
    this.store.next(newStore);
    this.updateCurrrencyValue(
      newStore?.tollCurrency as (typeof Common.TollCurrency)[keyof typeof Common.TollCurrency]
    );
    this.updateDistanceUnit(newStore?.distanceUnits);
    this.updateFuelUnit(newStore?.fuelUnits);
    const userCfg = this.userService.getUserCfg();
    this.updateSearchView(userCfg?.searchDefaultValue);
  }

  constructor(
    private http: HttpClient,
    private alertService: AlertService,
    private authService: AuthenticationService,
    private tripService: TripService,
    private searchService: SearchService,
    private dataversionService: DataVersionService,
    private userService: UserService
  ) {}

  getDefaultAppSettingsData(isEuRegion: boolean): RoutingProfile {
    return {
      LCV: false,
      MaxWeightPerAxleGroup: null,
      VehicleGroups: [],
      classOverrides: 0,
      congestionZone: 0,
      costGHG: '22.20',
      costMaintEmpty: '0.19',
      costMaintLoad: '0.19',
      costPerFuelUnit: '3.94',
      costTimeEmpty: '31.64',
      costTimeLoad: '31.64',
      dataVersion: 'current',
      default: true,
      displayRestrictions: 0,
      distanceUnits: 0,
      elevationLimit: null,
      environmentalZone: 0,
      exchangeRate: '1.051',
      ferryClosed: false,
      fiftyThreeFootTrailer: false,
      fuelEconomyEmpty: '6.48',
      fuelEconomyLoad: '6.48',
      fuelUnits: 0,
      governorSpeedLimit: null,
      hazmatType: 0,
      highwayOnly: false,
      hubRouting: false,
      includeFerryDistance: true,
      includeTollData: false,
      isFerryDiscouraged: false,
      maxHeight: 162,
      maxWidth: 96,
      nationalNetwork: false,
      numAxles: 5,
      overrideRestrictions: false,
      propane: false,
      routingProfileId: 0,
      sideOfStreetAdherence: 0,
      statusDeleted: false,
      tollCurrency: isEuRegion ? 11 : 0,
      tollDiscountPlans: '',
      tollDiscouraged: false,
      tollRoadType: 3,
      totalLength: 576,
      totalWeight: 80000,
      totalWeightPerAxle: 5,
      truckDimensions: 16,
      tunnelRestriction: null,
      ultraLowEmissionZone: null,
      unitsOfMeasure: 0,
      useAvoidFavors: false,
      useSites: false,
      useTraffic: false,
      vehicleType: 3,
      name: 'Default',
      bordersOpen: true,
      routingType: 0,
      euProfile: {
        emiType: 7,
        fuelConsumption: 0,
        fuelType: 4,
        hasTrailer: false,
        isHybrid: false,
        minPoll: false,
        refrigerated: false,
        trailerCount: 0,
        trailerMaxAxles: 1,
        trailerMaxHt: 162,
        trailerMaxWt: 80000,
        trailerType: 1,
      },
    };
  }

  setDefaultRoutingProfile(appSettingsData: RoutingProfile[]) {
    if (appSettingsData?.length) {
      const data: RoutingProfile = appSettingsData[0];
      this.store.next(data);
    }
  }

  updateDistanceUnit(distUnit: number) {
    this.selectedDistanceUnit.next(distUnit);
  }

  updateFuelUnit(fuelUnit: number) {
    this.selectedFuelUnit.next(fuelUnit);
  }

  updateCurrrencyValue(
    currency: (typeof Common.TollCurrency)[keyof typeof Common.TollCurrency]
  ) {
    this.selectedCurrency.next(currency);
  }

  updateSearchView(searchOption?: SearchView, reset = false) {
    if (reset) {
      const userCfg = this.userService.getDefaultUserCfg(
        this.authService?.currentUserValue
      );
      sessionStorage.setItem('searchView', JSON.stringify(searchOption));
      this.searchView.next(searchOption);
      this.userService.updateUserCfg(userCfg);
    } else {
      sessionStorage.setItem('searchView', JSON.stringify(searchOption));
      const userCfg = this.userService.getUserCfg();
      this.searchView.next(searchOption);
      userCfg.searchDefaultValue = searchOption;
      this.userService.updateUserCfg(userCfg);
    }
  }

  hasAppSettingsData() {
    return Boolean(this.storeValue);
  }

  getAppSettingsData(): Observable<RoutingProfile[]> {
    this.searchService.singleSearchOptions();
    this.isLoading.next(true);
    return this.http
      .get<RoutingProfile[]>(`${environment.daUrl}routing-profiles/default`)
      .pipe(
        switchMap((data) => {
          if (data?.length === 0) {
            // If No default route profile Options for the logged In user. we are creating a Routing Profile with default Options
            let defaultRouteProfileOptions: RoutingProfile =
              this.getDefaultAppSettingsData(
                this.authService?.currentUserValue?.region.toLowerCase() ===
                  Common.Region.EU
              );
            defaultRouteProfileOptions = this.customizeDefaultOptions(
              defaultRouteProfileOptions
            );
            //setting the routeProfileOptions to default for the user.
            defaultRouteProfileOptions.default = true;
            return this.tripService
              .createUserDefaultProfile(defaultRouteProfileOptions)
              .pipe(
                map((defaultProfile) => {
                  this.isLoading.next(false);
                  defaultRouteProfileOptions.routingProfileId =
                    defaultProfile?.Id;
                  return [defaultRouteProfileOptions];
                })
              );
          }
          this.isLoading.next(false);
          const defaultRouteProfileOptions = this.customizeDefaultOptions(
            data[0]
          );
          return of([defaultRouteProfileOptions]);
        }),
        catchError((err) => {
          let defaultRouteProfileOptions: RoutingProfile =
            this.getDefaultAppSettingsData(
              this.authService?.currentUserValue?.region.toLowerCase() ===
                Common.Region.EU
            );
          defaultRouteProfileOptions = this.customizeDefaultOptions(
            defaultRouteProfileOptions
          );
          this.isLoading.next(false);
          const errorMessage =
            'Due to a technical issue, your saved app settings options are temporarily unavailable,please try again later. Default options are available at this time.';
          this.alertService.error(
            `${errorMessage}`,
            true,
            'app-modal-alert',
            true
          );
          return of([defaultRouteProfileOptions]);
        })
      );
  }

  retrieveAppSettingsDataSubscription(): Subscription {
    return this.getAppSettingsData().subscribe((data) => {
      if (data?.length) {
        this.setStore(data[0]);
      }
    });
  }

  updateAppSettingsData(data: RoutingProfile, reset = false) {
    this.isLoading.next(true);
    return this.http
      .put<DataUpdated>(
        `${environment.daUrl}routing-profiles/${data.routingProfileId}`,
        data
      )
      .pipe(
        tap(() => {
          if (data) {
            if (reset) {
              this.updateSearchView(null, true);
            }
            this.setStore(data);
          }
          this.isLoading.next(false);
          return true;
        }),
        catchError((err) => this.handleError(err))
      );
  }

  handleError(err): Observable<boolean> {
    this.isLoading.next(false);
    const error = err?.Description
      ? err?.Description
      : err?.error?.toString().split(':')[1];
    this.alertService.error(
      error?.substring(0, error.indexOf('.')),
      false,
      'app-modal-alert'
    );
    return of(false);
  }
  customizeDefaultOptions(defaultRouteProfileOptions) {
    // if (
    //   this.authService?.currentUserValue?.subscriptionLevel ===
    //   SubscriptionLevel.PREMIUM
    // ) {
    //   defaultRouteProfileOptions.includeTollData = true;
    // }

    this.dataVersionList$.subscribe((data) => {
      this.dataVersions = data;
      if (this.dataVersions.length > 0) {
        const nonCurrentVersion = this.dataVersions?.find((item) => {
          return item.value !== 'current';
        });

        if (
          this.authService?.currentUserValue?.subscriptionLevel ===
          SubscriptionLevel.STANDARD
        ) {
          defaultRouteProfileOptions.dataVersion =
            nonCurrentVersion?.value as string;
          defaultRouteProfileOptions.highwayOnly = true;
          defaultRouteProfileOptions.useTraffic = false;
        }
      }
    });

    return defaultRouteProfileOptions;
  }
}
